import { useId, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Mui, useTheme } from "@leap4/frontend";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { IReport } from "../report.interface";
import { eventColumnKeyFromEventName, eventNameFromPredictedMetricKey, isPredictedMetricKey } from "../reports";
import { seriesColors, numericFontFamily } from "../../../../theme/colors";
import { formatMetricValue } from "../../dashboard/PerformanceChart";
import { DashboardLoadingChart } from "../../dashboard/DashboardLoadingChart";
import { RunTimer } from "../base/runTimer";

const { Box, Button, Paper, Tooltip: MuiTooltip, Typography, alpha, lighten } = Mui;

/** One point of the prediction series, as the backend returns it. */
export interface IPredictionSeriesPoint {
    pixelDate: string;
    basis: string;
    expectedTotal: number;
    standardError?: number | null;
    /** Credit observed so far. Null for forecast dates, which have no observations at all. */
    soFar?: number | null;
}

/** One event's series, kept apart so each event gets its own colour, label and window. */
export interface IPredictionEventSeries {
    event: string;
    points: IPredictionSeriesPoint[];
}

interface IProps {
    /** The report grouped by date alone, unpaginated: never the grid's page, which is a slice. */
    rows: Array<Record<string, any>>;
    loading: boolean;
    report: IReport;
    /** Column name to label, as resolved for the grid, so the chart names things the same way. */
    columnLabels: Map<string, string>;
    predictionSeries?: IPredictionEventSeries[];
    /** Extend the axis with the forecast, when the range reaches the present. */
    showForecast?: boolean;
    predictionError?: string | null;
}

interface IChartMetric {
    key: string;
    label: string;
    color: string;
    decimals: number;
    kind: "bar" | "line" | "predicted";
    /** The attributed column of the same event: same units, so a prediction shares its axis. */
    sibling?: string;
}

const HEIGHT = 340;
const SERVED = "served_impression_count";
const DATE_COLUMNS = ["pixel_date", "event_date"];
const PALETTE = ["#F59E0B", "#10B981", "#EF4444", "#06B6D4", "#A855F7", "#EC4899", "#84CC16", "#F97316"];
const DEFAULT_VISIBLE = 3;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ymd = (d: unknown) => String(d ?? "").slice(0, 10);

/** Every bucket the report asked for, whether or not a row came back for it, so a gap reads as a gap. */
const rangeBuckets = (from: string, to: string, monthly: boolean): string[] => {
    if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || to < from) return [];
    const out: string[] = [];
    let cursor = dayjs(monthly ? `${from.slice(0, 7)}-01` : from);
    const last = dayjs(to);
    while (!cursor.isAfter(last) && out.length < 1000) {
        out.push(cursor.format("YYYY-MM-DD"));
        cursor = cursor.add(1, monthly ? "month" : "day");
    }
    return out;
};

const formatAxis = (value: number): string => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const decimalsFor = (key: string, values: number[]): number => {
    if (key === SERVED || key === "reach") return 0;
    if (/frequency/i.test(key)) return 2;
    return values.some((v) => !Number.isInteger(v)) ? 1 : 0;
};

type ChartPoint = { key: string } & Record<string, number | null | [number, number] | string>;

interface ITooltipProps {
    active?: boolean;
    payload?: Array<{ payload?: ChartPoint }>;
    metrics: IChartMetric[];
    labelFor: (key: string) => string;
}

const ChartTooltip = ({ active, payload, metrics, labelFor }: ITooltipProps) => {
    const point = payload?.[0]?.payload;
    if (!active || !point) return null;
    return (
        <Paper elevation={3} sx={{ p: 1.5, minWidth: 200 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                {labelFor(point.key)}
            </Typography>
            {metrics.map((m) => {
                const value = (point[m.key] ?? point[`${m.key}__fc`]) as number | null | undefined;
                const error = point[`${m.key}__err`] as number | null | undefined;
                const forecast = point[`${m.key}__fc`] != null && point[m.key] == null;
                return (
                    <Box key={m.key} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                        <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: m.color, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
                            {m.label}
                            {forecast ? " · forecast" : ""}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: numericFontFamily, fontVariantNumeric: "tabular-nums" }}>
                            {typeof value === "number" ? formatMetricValue(value, m.decimals) : "—"}
                            {typeof value === "number" && typeof error === "number" && value > 0 ? ` ±${Math.round((error / value) * 100)}%` : ""}
                        </Typography>
                    </Box>
                );
            })}
        </Paper>
    );
};

/**
 * The report over its own date range: one line per metric, each on its own scale, toggled from the
 * legend. Served impressions are columns, as on the dashboard. Predictions carry their margin of error
 * as a band; the forecast past the range is shown only when asked for, never by default.
 */
const AreaKey = ({ label, sx }: { label: string; sx: object }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box aria-hidden="true" sx={{ width: 14, height: 10, borderRadius: "2px", border: 1, borderColor: "divider", ...sx }} />
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
);

const ReportChart = ({ rows, loading, report, columnLabels, predictionSeries = [], predictionError = null, showForecast = false }: IProps): JSX.Element | null => {
    const theme = useTheme();
    const patternId = `report-maturing-${useId().replace(/:/g, "")}`;
    const monthly = report.timeAggregation === "Month";
    const bucketOf = (date: string) => (monthly ? `${date.slice(0, 7)}-01` : date);
    const today = dayjs().format("YYYY-MM-DD");
    const rowDates = rows.flatMap((r) => DATE_COLUMNS.map((c) => ymd(r[c])).filter((d) => ISO_DATE.test(d))).sort();
    // The picked range when there is one; otherwise whatever the rows cover.
    const start = ISO_DATE.test(ymd(report.startDate)) ? ymd(report.startDate) : rowDates[0] ?? "";
    const end = ISO_DATE.test(ymd(report.endDate)) ? ymd(report.endDate) : rowDates[rowDates.length - 1] ?? "";

    const [visibleOverride, setVisibleOverride] = useState<string[] | null>(null);

    const dateColumn = DATE_COLUMNS.find((c) => rows.some((r) => ISO_DATE.test(ymd(r[c])))) ?? null;

    // Report order, as the grid shows it; predicted columns come from the series, not the rows.
    const plainKeys = useMemo(() => {
        const numeric = new Set<string>();
        rows.forEach((r) => Object.entries(r).forEach(([k, v]) => !DATE_COLUMNS.includes(k) && typeof v === "number" && !k.startsWith("predicted_") && !/error_pct$/.test(k) && numeric.add(k)));
        const ordered = Array.from(columnLabels.keys()).filter((k) => numeric.has(k));
        return ordered.concat(Array.from(numeric).filter((k) => !ordered.includes(k)));
    }, [rows, columnLabels]);

    const predictedLabel = (event: string): string => {
        const picked = report.metrics.find((m) => isPredictedMetricKey(m.eventType) && eventNameFromPredictedMetricKey(m.eventType) === event);
        return picked?.label || columnLabels.get(`predicted_${eventColumnKeyFromEventName(event).replace(/^event_/, "")}`) || `Predicted ${event}`;
    };

    const forecastEnd = useMemo(() => {
        const dates = predictionSeries.flatMap((s) => s.points.filter((p) => p.basis === "forecast").map((p) => ymd(p.pixelDate)));
        return dates.length ? dates.sort()[dates.length - 1] : null;
    }, [predictionSeries]);
    // Only a range that reaches the present has a future to continue into.
    const forecastAvailable = !!forecastEnd && forecastEnd > end && start <= today && end >= today;
    const axisEnd = showForecast && forecastAvailable && forecastEnd ? forecastEnd : end;

    const { data, metrics, firstOpenBy, maturingTo, forecastFrom } = useMemo(() => {
        const buckets = rangeBuckets(start, axisEnd, monthly);
        const byBucket = new Map<string, ChartPoint>(buckets.map((b) => [b, { key: b }]));

        const plainValues = new Map<string, number[]>();
        if (dateColumn) {
            for (const row of rows) {
                const point = byBucket.get(bucketOf(ymd(row[dateColumn])));
                if (!point) continue;
                for (const k of plainKeys) {
                    const v = row[k];
                    if (typeof v !== "number" || !Number.isFinite(v)) continue;
                    point[k] = ((point[k] as number | null) ?? 0) + v;
                    plainValues.set(k, [...(plainValues.get(k) ?? []), v]);
                }
            }
        }

        const eventColor = new Map<string, string>();
        let paletteIndex = 0;
        const nextColor = () => PALETTE[paletteIndex++ % PALETTE.length];

        const out: IChartMetric[] = plainKeys.map((k) => {
            const color = k === SERVED ? seriesColors.impressions : nextColor();
            if (k.startsWith("event_")) eventColor.set(k, color);
            return { key: k, label: columnLabels.get(k) ?? k, color, decimals: decimalsFor(k, plainValues.get(k) ?? []), kind: k === SERVED ? "bar" : "line" };
        });

        const firstOpenBy = new Map<string, string>();
        let firstForecast: string | null = null;
        predictionSeries.forEach((s) => {
            const key = `pred__${eventColumnKeyFromEventName(s.event)}`;
            // Same hue as the event's attributed line, lighter, so the pair reads as one metric in two states.
            const sibling = eventColor.get(eventColumnKeyFromEventName(s.event));
            const color = sibling ? lighten(sibling, 0.35) : nextColor();
            const agg = new Map<string, { total: number; error: number; forecast: boolean; open: boolean }>();
            for (const p of s.points) {
                // A forecast only ever starts today; earlier dates read as still maturing.
                const basis = p.basis === "forecast" && ymd(p.pixelDate) < today ? "projected" : p.basis;
                const b = bucketOf(ymd(p.pixelDate));
                if (!byBucket.has(b)) continue;
                const prev = agg.get(b) ?? { total: 0, error: 0, forecast: true, open: false };
                agg.set(b, {
                    total: prev.total + (p.expectedTotal || 0),
                    error: prev.error + (p.standardError || 0),
                    forecast: prev.forecast && basis === "forecast",
                    open: prev.open || basis !== "actual",
                });
            }
            const ordered = buckets.filter((b) => agg.has(b));
            ordered.forEach((b, i) => {
                const a = agg.get(b)!;
                const point = byBucket.get(b)!;
                const nextIsForecast = !a.forecast && agg.get(ordered[i + 1] ?? "")?.forecast;
                if (!a.forecast) point[key] = a.total;
                // The last measured point is repeated on the forecast line so the two join.
                if (a.forecast || nextIsForecast) point[`${key}__fc`] = a.total;
                if (a.error > 0) {
                    point[`${key}__band`] = [Math.max(0, a.total - a.error), a.total + a.error];
                    point[`${key}__err`] = a.error;
                }
                if (a.open && !a.forecast && (!firstOpenBy.get(key) || b < firstOpenBy.get(key)!)) firstOpenBy.set(key, b);
                if (a.forecast && (!firstForecast || b < firstForecast)) firstForecast = b;
            });
            out.push({ key, label: predictedLabel(s.event), color, decimals: 1, kind: "predicted", sibling: sibling ? eventColumnKeyFromEventName(s.event) : undefined });
        });

        const lastPast = buckets.filter((b) => b <= bucketOf(today)).pop() ?? null;
        return {
            data: buckets.map((b) => byBucket.get(b)!),
            metrics: out,
            firstOpenBy,
            maturingTo: lastPast,
            forecastFrom: firstForecast as string | null,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, plainKeys, predictionSeries, start, axisEnd, monthly, dateColumn, columnLabels, today]);

    // Report order, but a prediction always makes the cut: the gap to it is what the chart is for.
    const firstPredicted = metrics.find((m) => m.kind === "predicted");
    const leading = metrics.filter((m) => m !== firstPredicted).slice(0, firstPredicted ? DEFAULT_VISIBLE - 1 : DEFAULT_VISIBLE);
    const defaultVisible = metrics.filter((m) => leading.includes(m) || m === firstPredicted).map((m) => m.key);
    const visibleKeys = (visibleOverride ?? defaultVisible).filter((k) => metrics.some((m) => m.key === k));
    const visible = metrics.filter((m) => visibleKeys.includes(m.key));
    const toggle = (key: string) => setVisibleOverride(visibleKeys.includes(key) ? visibleKeys.filter((k) => k !== key) : metrics.map((m) => m.key).filter((k) => k === key || visibleKeys.includes(k)));

    const labelFor = (key: string) => (monthly ? dayjs(key).format("MMM YYYY") : dayjs(key).format("ddd, MMM D, YYYY"));
    const tickFor = (key: string) => (monthly ? dayjs(key).format("MMM YYYY") : dayjs(key).format("MMM D"));
    const axisColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const showDots = data.length <= 45;
    const hasConstraints = (report.constraints || []).some((c) => !!c.metric && c.value !== "");
    const ordered = [...visible.filter((m) => m.kind === "bar"), ...visible.filter((m) => m.kind !== "bar")];
    // Each event's own lookback decides how far back it is still open; hatch for the predictions on show.
    const openDates = (keys: string[]) => keys.map((k) => firstOpenBy.get(k)).filter((d): d is string => !!d).sort();
    const visiblePredicted = visible.filter((m) => m.kind === "predicted").map((m) => m.key);
    const maturingFrom = (visiblePredicted.length ? openDates(visiblePredicted) : openDates(Array.from(firstOpenBy.keys())))[0] ?? null;
    const axisOf = (m: IChartMetric) => (m.sibling && visibleKeys.includes(m.sibling) ? m.sibling : m.key);
    // One axis per unit, coloured like the first series on it.
    const axes = ordered.filter((m, i) => ordered.findIndex((o) => axisOf(o) === axisOf(m)) === i).map((m) => ({ id: axisOf(m), color: m.color }));

    if (loading && rows.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: "12px" }}>
                <Typography variant="subtitle2" component="h3" sx={{ mb: 1.5 }}>Over time</Typography>
                <DashboardLoadingChart height={HEIGHT} label={<>Loading the chart… <RunTimer /></>} />
            </Paper>
        );
    }

    if (metrics.length === 0 || data.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: "12px" }}>
                <Typography variant="body2" color="text.secondary">
                    {predictionError ? `Prediction series unavailable: ${predictionError}` : "Nothing to chart for this range."}
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
                <Typography variant="subtitle2" component="h3">
                    Over time
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {monthly ? "By month" : "By day"} · {dayjs(start).format("MMM D")} – {dayjs(axisEnd).format("MMM D, YYYY")}
                </Typography>
                <Box sx={{ ml: "auto", display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {maturingFrom && maturingTo && maturingFrom <= maturingTo && (
                        <AreaKey label={`Still maturing from ${dayjs(maturingFrom).format("MMM D")}`} sx={{ backgroundImage: `repeating-linear-gradient(45deg, ${axisColor} 0 1px, transparent 1px 4px)`, opacity: 0.6 }} />
                    )}
                    {forecastFrom && showForecast && <AreaKey label="Forecast" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.25) }} />}
                </Box>
            </Box>

            {!visible.length ? (
                <Box sx={{ height: HEIGHT, display: "grid", placeItems: "center" }}>
                    <Typography color="text.secondary">Pick a metric below to chart it</Typography>
                </Box>
            ) : (
                <Box role="region" aria-label="Report chart" sx={{ width: "100%", height: HEIGHT, opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}>
                    <ResponsiveContainer>
                        <ComposedChart data={data} margin={{ top: 16, right: 8, bottom: 0, left: 0 }} barCategoryGap="25%" accessibilityLayer>
                            <defs>
                                <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                    <rect width="8" height="8" fill={theme.palette.action.hover} />
                                    <line x1="0" y1="0" x2="0" y2="8" stroke={theme.palette.text.disabled} strokeWidth="1.5" strokeOpacity="0.4" />
                                </pattern>
                            </defs>
                            <CartesianGrid yAxisId={axes[0].id} strokeDasharray="4 4" stroke={gridColor} vertical={false} />
                            <XAxis dataKey="key" tickFormatter={tickFor} tick={{ fontSize: 12, fill: axisColor, fontFamily: numericFontFamily }} axisLine={{ stroke: gridColor }} tickLine={false} minTickGap={28} />
                            {axes.map((m, i) => (
                                <YAxis
                                    key={m.id}
                                    yAxisId={m.id}
                                    orientation={i === 1 ? "right" : "left"}
                                    hide={i > 1}
                                    domain={[0, "auto"]}
                                    // A hidden axis still reserves its width and pushes the visible one off the edge.
                                    width={i > 1 ? 0 : 56}
                                    // Coloured like its series, which names the axis without a title to crop.
                                    tick={{ fontSize: 11, fill: m.color, fontFamily: numericFontFamily }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={formatAxis}
                                />
                            ))}
                            {maturingFrom && maturingTo && maturingFrom <= maturingTo && (
                                <ReferenceArea yAxisId={axes[0].id} x1={maturingFrom} x2={maturingTo} fill={`url(#${patternId})`} fillOpacity={1} stroke="none" ifOverflow="extendDomain" />
                            )}
                            {forecastFrom && showForecast && (
                                <ReferenceArea yAxisId={axes[0].id} x1={forecastFrom} x2={axisEnd} fill={theme.palette.primary.main} fillOpacity={0.06} stroke="none" ifOverflow="extendDomain" />
                            )}
                            <Tooltip content={<ChartTooltip metrics={visible} labelFor={labelFor} />} cursor={{ fill: theme.palette.action.hover }} />
                            {ordered.map((m) =>
                                m.kind === "bar" ? (
                                    <Bar key={m.key} dataKey={m.key} yAxisId={axisOf(m)} name={m.label} fill={m.color} fillOpacity={0.45} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />
                                ) : m.kind === "predicted" ? (
                                    [
                                        <Area key={`${m.key}-band`} dataKey={`${m.key}__band`} yAxisId={axisOf(m)} stroke="none" fill={m.color} fillOpacity={0.16} isAnimationActive={false} activeDot={false} connectNulls={false} />,
                                        <Line key={m.key} dataKey={m.key} yAxisId={axisOf(m)} name={m.label} type="monotone" stroke={m.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />,
                                        <Line key={`${m.key}-fc`} dataKey={`${m.key}__fc`} yAxisId={axisOf(m)} name={`${m.label} (forecast)`} type="monotone" stroke={m.color} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />,
                                    ]
                                ) : (
                                    <Line key={m.key} dataKey={m.key} yAxisId={axisOf(m)} name={m.label} type="monotone" stroke={m.color} strokeWidth={2} dot={showDots ? { r: 2.5, fill: m.color, strokeWidth: 0 } : false} activeDot={{ r: 5, stroke: theme.palette.background.paper, strokeWidth: 2 }} isAnimationActive={false} />
                                ),
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            )}

            <Box role="group" aria-label="Chart metrics" sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", columnGap: 0.5, pt: 1 }}>
                {metrics.map((m) => {
                    const active = visibleKeys.includes(m.key);
                    return (
                        <MuiTooltip key={m.key} title={active ? "Hide from the chart" : "Show on the chart"} describeChild>
                            <Button
                                size="small"
                                aria-pressed={active}
                                onClick={() => toggle(m.key)}
                                sx={{ minWidth: 0, minHeight: 32, px: 0.75, gap: 0.5, fontSize: 12, fontWeight: 400, textTransform: "none", color: active ? "text.primary" : "text.secondary", opacity: active ? 1 : 0.6, "&:hover": { bgcolor: "transparent", opacity: 1 } }}
                            >
                                <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: m.kind === "bar" ? "2px" : "50%", bgcolor: m.color, flexShrink: 0 }} />
                                {m.label}
                            </Button>
                        </MuiTooltip>
                    );
                })}
                {visibleOverride && (
                    <Button size="small" onClick={() => setVisibleOverride(null)} sx={{ minWidth: 0, minHeight: 32, ml: 1, px: 0.75, fontSize: 11, fontWeight: 400, textTransform: "none", color: "text.secondary" }}>
                        Reset
                    </Button>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: "center", mt: 0.5, mb: 0 }}>
                {[
                    visible.length > 1 && "Each metric has its own scale.",
                    visible.some((m) => m.kind === "predicted") && "Shaded band: margin of error.",
                    maturingFrom && "Hatched dates can still grow.",
                    hasConstraints && "Constraints apply to the table only.",
                ]
                    .filter(Boolean)
                    .join(" ")}
            </Typography>

            {predictionError && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5, textAlign: "center" }}>
                    Prediction series unavailable: {predictionError}
                </Typography>
            )}
        </Paper>
    );
};

export default ReportChart;
